import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  FeaturebaseProvider,
  useChangelogWidget,
  useFeaturebase,
  useFeedbackWidget,
} from 'featurebase-js/react'
import type {
  GetFeaturebaseIdentity,
  GetFeaturebaseIdentityVariables,
} from 'types/graphql'

import { TypedDocumentNode, useQuery } from '@cedarjs/web'

import { useEmrAuth } from 'src/auth'
import { createNamedContext } from 'src/utils'
import { appType } from 'src/utils/appType'

/**
 * Featurebase workspace (organization) id — drives the embedded portal,
 * messenger, and changelog. Public by design; the JWT signing secret used to
 * identify users stays server-side (see `featurebaseIdentity` resolver).
 */
export const FEATUREBASE_APP_ID = '6a3adcf65c9b2666c80236c2'

/**
 * Featurebase board names for `setBoard`. The widget API takes the board's
 * short name as it appears in Featurebase, not the dashboard ObjectId.
 */
export const FEATUREBASE_FEEDBACK_BOARDS = {
  ASK_DEVELO: 'Ask Develo',
  RECORD_PAYMENT: 'Record Payment',
  TRANSACTIONS_UNIFIED_BILLING: 'Transactions & Unified Billing',
} as const

export type FeaturebaseFeedbackBoard =
  (typeof FEATUREBASE_FEEDBACK_BOARDS)[keyof typeof FEATUREBASE_FEEDBACK_BOARDS]

/**
 * Opens the already-booted feedback widget on a specific board. Featurebase
 * listens for this postMessage on the host window (no extra SDK call).
 */
export const openFeaturebaseFeedbackWidget = (
  board: FeaturebaseFeedbackBoard
) => {
  window.postMessage({
    target: 'FeaturebaseWidget',
    data: {
      action: 'openFeedbackWidget',
      setBoard: board,
    },
  })
}

// `getUnviewedCount()` is a synchronous read, not reactive, so poll it to keep
// the nav badge fresh. Messenger unread, by contrast, re-renders on its own.
const CHANGELOG_POLL_MS = 60_000

const FEATUREBASE_IDENTITY_QUERY: TypedDocumentNode<
  GetFeaturebaseIdentity,
  GetFeaturebaseIdentityVariables
> = gql`
  query GetFeaturebaseIdentity {
    featurebaseIdentity {
      jwt
    }
  }
`

interface FeaturebaseWidgetContextValue {
  isEnabled: boolean
  /** Unread support replies (messenger) — replies to the user's threads. */
  unreadReplies: number
  /** Unviewed product updates (changelog). */
  unviewedChangelog: number
  /** Combined count (unreadReplies + unviewedChangelog). */
  updateCount: number
  /** Mark product updates as viewed — call when the user opens a changelog surface. */
  markUpdatesViewed: () => void
  /** Open the changelog as a fullscreen popup (optionally a specific release slug). */
  openChangelogPopup: (slug?: string) => void
  /** Open the feedback widget, preselecting a board. */
  openFeedbackWidget: (board: FeaturebaseFeedbackBoard) => void
}

const DISABLED_VALUE: FeaturebaseWidgetContextValue = {
  isEnabled: false,
  unreadReplies: 0,
  unviewedChangelog: 0,
  updateCount: 0,
  markUpdatesViewed: () => {},
  openChangelogPopup: () => {},
  openFeedbackWidget: () => {},
}

const FeaturebaseWidgetContext =
  createNamedContext<FeaturebaseWidgetContextValue>(
    'FeaturebaseWidgetContext',
    DISABLED_VALUE
  )

export const useFeaturebaseWidget = () => {
  const context = useContext(FeaturebaseWidgetContext)
  if (!context) {
    throw new Error(
      'useFeaturebaseWidget must be used within a FeaturebaseWidgetProvider'
    )
  }
  return context
}

/**
 * Gate Featurebase here so descendants never have to check `isEnabled`: the
 * integration only boots for the EMR build (`App.tsx` is compiled into three
 * apps — emr / patientPortal / admin) and only for users with the FEATUREBASE
 * toggle. Logged-out / disabled users get a no-op context, which also means the
 * Featurebase SDK tears down cleanly on logout (the Provider below unmounts).
 */
export const FeaturebaseWidgetProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { currentUser } = useEmrAuth()
  const isEnabled =
    appType === 'emr' && !!currentUser?.featureToggles.includes('FEATUREBASE')

  if (!isEnabled || !currentUser) {
    return (
      <FeaturebaseWidgetContext.Provider value={DISABLED_VALUE}>
        {children}
      </FeaturebaseWidgetContext.Provider>
    )
  }

  return (
    <EnabledFeaturebaseWidgetProvider>
      {children}
    </EnabledFeaturebaseWidgetProvider>
  )
}

const EnabledFeaturebaseWidgetProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  // Identity is per-user; bypass the cache so a logout/login at the same
  // browser can't seed the SDK with the previous user's JWT.
  const { data } = useQuery(FEATUREBASE_IDENTITY_QUERY, {
    fetchPolicy: 'no-cache',
  })

  // Undefined while loading (anonymous boot) → re-identifies once the JWT
  // arrives, without re-booting the messenger. Stays undefined if the server
  // has no signing secret configured, in which case surfaces stay anonymous.
  const featurebaseJwt = data?.featurebaseIdentity?.jwt ?? undefined

  return (
    // hideDefaultLauncher keeps the messenger booted — so its unread count
    // still feeds the nav badge — without rendering a floating chat bubble
    // (we surface "unread replies" on the Feedback nav item instead, and the
    // existing Pylon widget already owns the on-screen chat bubble).
    <FeaturebaseProvider
      appId={FEATUREBASE_APP_ID}
      featurebaseJwt={featurebaseJwt}
      hideDefaultLauncher
    >
      <FeaturebaseRuntime>{children}</FeaturebaseRuntime>
    </FeaturebaseProvider>
  )
}

const FeaturebaseRuntime = ({ children }: { children: ReactNode }) => {
  // Reactive: re-renders when a support reply lands.
  const { unreadCount } = useFeaturebase()

  // Boot the standalone changelog widget. `dropdown.enabled` initializes the
  // widget runtime so `getUnviewedCount()` works for the nav badge (there is no
  // dropdown anchor rendered). `popup.enabled` + `autoOpenForNewUpdates` makes
  // the fullscreen "Release notes" popup open automatically on page load
  // whenever a new update has been published, and also lets us open it on
  // demand from the nav item.
  const { getUnviewedCount, markAllAsViewed, openPopup } = useChangelogWidget({
    theme: 'light',
    dropdown: { enabled: true },
    popup: { enabled: true, autoOpenForNewUpdates: true },
  })

  // Boot the standalone feedback widget. No `placement` → no floating button;
  // a `[data-featurebase-feedback]` nav button opens the panel instead.
  useFeedbackWidget({ theme: 'light' })

  const [unviewedChangelog, setUnviewedChangelog] = useState(0)

  // Hold the latest getUnviewedCount in a ref so the polling interval is set up
  // once — otherwise a new function identity from the SDK on re-render would
  // tear down and restart the interval, resetting its timer each time.
  const getUnviewedCountRef = useRef(getUnviewedCount)
  useEffect(() => {
    getUnviewedCountRef.current = getUnviewedCount
  })

  useEffect(() => {
    const read = () => setUnviewedChangelog(getUnviewedCountRef.current() ?? 0)
    read()
    const intervalId = setInterval(read, CHANGELOG_POLL_MS)
    return () => clearInterval(intervalId)
  }, [])

  const markUpdatesViewed = useCallback(() => {
    markAllAsViewed()
    setUnviewedChangelog(0)
  }, [markAllAsViewed])

  const openChangelogPopup = useCallback(
    (slug?: string) => openPopup(slug ? { slug } : undefined),
    [openPopup]
  )

  const openFeedbackWidget = useCallback(
    (board: FeaturebaseFeedbackBoard) => openFeaturebaseFeedbackWidget(board),
    []
  )

  const value: FeaturebaseWidgetContextValue = {
    isEnabled: true,
    unreadReplies: unreadCount,
    unviewedChangelog,
    updateCount: unreadCount + unviewedChangelog,
    markUpdatesViewed,
    openChangelogPopup,
    openFeedbackWidget,
  }

  return (
    <FeaturebaseWidgetContext.Provider value={value}>
      {children}
    </FeaturebaseWidgetContext.Provider>
  )
}

export default FeaturebaseWidgetContext
